/**
 * ArrayMinMax - Utility for finding minimum and maximum values in arrays
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for finding minimum and maximum values in arrays,
 * with proper handling of empty arrays and edge cases.
 */

/**
 * Find the minimum value in an array of numbers
 * 
 * Returns the smallest value in the array.
 * Returns null if the array is empty.
 * 
 * @param values - Array of numbers
 * @returns Minimum value, or null if array is empty
 */
export function findMinValue(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  
  return Math.min(...values);
}

/**
 * Find the maximum value in an array of numbers
 * 
 * Returns the largest value in the array.
 * Returns null if the array is empty.
 * 
 * @param values - Array of numbers
 * @returns Maximum value, or null if array is empty
 */
export function findMaxValue(values: number[]): number | null {
  if (values.length === 0) {
    return null;
  }
  
  return Math.max(...values);
}

/**
 * Find the minimum value in an array of numbers with a default fallback
 * 
 * Returns the smallest value in the array, or the default value if array is empty.
 * 
 * @param values - Array of numbers
 * @param defaultValue - Default value to return if array is empty
 * @returns Minimum value, or defaultValue if array is empty
 */
export function findMinValueWithDefault(values: number[], defaultValue: number): number {
  if (values.length === 0) {
    return defaultValue;
  }
  
  return Math.min(...values);
}

/**
 * Find the maximum value in an array of numbers with a default fallback
 * 
 * Returns the largest value in the array, or the default value if array is empty.
 * 
 * @param values - Array of numbers
 * @param defaultValue - Default value to return if array is empty
 * @returns Maximum value, or defaultValue if array is empty
 */
export function findMaxValueWithDefault(values: number[], defaultValue: number): number {
  if (values.length === 0) {
    return defaultValue;
  }
  
  return Math.max(...values);
}

